import React, { useState, useMemo, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Sidebar } from '../../../components/sidebar';
import { Post, PostFormData } from '../types';
import { mockPosts } from '../mocks/postsData';
import { EditProfileModal, type Profile } from '../../profiles-tracking/components/edit-profile-modal';
import { profilesTrackingService } from '../../profiles-tracking/services';
import { useEditProfileModal } from '../../../contexts/useEditProfileModal';
import { useUserStore, canEditSelectedProfile } from '../../../store/user';
import {
  PostsContainer,
  MainContent,
  Header,
  HeaderTop,
  HeaderTitle,
  Title,
  Subtitle,
  CalendarControls,
  MonthNavigation,
  NavButton,
  CurrentMonth,
  TodayButton,
  ViewButtons,
  ViewButton,
  CalendarWrapper,
  Calendar,
  CalendarHeader,
  WeekDay,
  CalendarGrid,
  DayCell,
  DayNumber,
  PostIndicators,
  PostBadge,
  ModalOverlay,
  ModalContainer,
  ModalLeft,
  ModalHeader,
  ModalTitle,
  CloseButton,
  TabsContainer,
  Tab,
  ModalBody,
  FormSection,
  Label,
  TextArea,
  CharCounter,
  ImageUploadArea,
  UploadIcon,
  UploadText,
  UploadHint,
  AIImageButton,
  ImagePreviewContainer,
  ImagePreview,
  RemoveImageButton,
  AIButtonsContainer,
  AIButton,
  SecondaryButton,
  Select,
  DateTimeGroup,
  Input,
  ModalRight,
  PreviewHeader,
  PreviewTitle,
  PreviewBody,
  PostPreviewCard,
  PostPreviewHeader,
  ProfileIcon,
  ProfileInfo,
  ProfileName,
  PostDate,
  PostPreviewContent,
  PostText,
  PostImages,
  EmptyPreview,
  ModalFooter,
  FooterLeft,
  FooterRight,
  CancelButton,
  SaveButton,
  ViewDropdown,
  DropdownButton,
  DropdownMenu,
  DropdownItem,
  ListView,
  ListHeader,
  ListTitle,
  AddButton,
  PostsList,
  PostCard,
  PostCardHeader,
  PostCardInfo,
  PostCardDate,
  PostCardTime,
  PostCardStatus,
  PostCardContent,
  PostCardImages,
  PostCardImage,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText,
  TimeGridContainer,
  TimeGridScroll,
  TimeGridHeader,
  TimeGridHeaderCell,
  TimeGridBody,
  TimeGridRow,
  TimeSlot,
  DayColumn,
  TimePost,
  TimePostTime,
  TimePostText,
} from './styles';

type ViewMode = 'calendar' | 'list';
type CalendarView = 'day' | 'week' | 'month';

export const PostsPage: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>(mockPosts);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  const [calendarView, setCalendarView] = useState<CalendarView>('month');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'update' | 'offer' | 'event'>('update');
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const { isOpen: isEditModalOpen, closeModal } = useEditProfileModal();
  const [profileData, setProfileData] = useState<Profile | null>(null);
  const { user } = useUserStore();
  const canEdit = canEditSelectedProfile(user);

  // Inicializar perfil com dados do usuário
  useEffect(() => {
    if (user) {
      const userProfile: Profile = {
        id: user.businessProfiles?.[0] || "1",
        name: user.name || "",
        category: "Cliente",
        status: "active" as const,
        stats: {
          views: 0,
          clicks: 0,
          ranking: 0,
        },
      };
      setProfileData(userProfile);
    }
  }, [user]);
  
  // Form state
  const [formData, setFormData] = useState<Partial<PostFormData>>({
    text: '',
    images: [],
    tone: 'automatic',
    emoticons: 'automatic',
    wordCount: 25,
    status: 'scheduled',
    profileName: 'Bill Car Rent a Car'
  });

  // Campos específicos para Oferta
  const [offerData, setOfferData] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    couponCode: '',
    redeemLink: '',
    terms: ''
  });

  // Campos específicos para Evento
  const [eventData, setEventData] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: ''
  });

  const [generatingAI, setGeneratingAI] = useState(false);

  // Calendário
  const monthNames = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
  ];

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  const getDisplayTitle = () => {
    if (calendarView === 'day') {
      return currentDate.toLocaleDateString('pt-BR', { 
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        weekday: 'long'
      });
    } else if (calendarView === 'week') {
      const weekStart = new Date(currentDate);
      const day = currentDate.getDay();
      weekStart.setDate(currentDate.getDate() - day);
      
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      return `${weekStart.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })} - ${weekEnd.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}`;
    } else {
      return `${monthNames[currentDate.getMonth()]} de ${currentDate.getFullYear()}`;
    }
  };

  const currentMonthYear = getDisplayTitle();

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: Array<{ date: Date; isCurrentMonth: boolean }> = [];

    // Dias do mês anterior
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false
      });
    }

    // Dias do mês atual
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }

    // Dias do próximo mês
    const remainingDays = 42 - days.length; // 6 semanas * 7 dias
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }

    return days;
  };

  // Funções para diferentes visualizações
  const getWeekDays = (date: Date) => {
    const day = date.getDay();
    const diff = date.getDate() - day; // Domingo da semana atual
    const sunday = new Date(date);
    sunday.setDate(diff);

    const days: Array<{ date: Date; isCurrentMonth: boolean }> = [];
    for (let i = 0; i < 7; i++) {
      const currentDay = new Date(sunday);
      currentDay.setDate(sunday.getDate() + i);
      days.push({
        date: currentDay,
        isCurrentMonth: currentDay.getMonth() === date.getMonth()
      });
    }
    return days;
  };

  const getSingleDay = (date: Date) => {
    return [{
      date: new Date(date),
      isCurrentMonth: true
    }];
  };

  const getDisplayDays = useMemo(() => {
    if (calendarView === 'day') {
      return getSingleDay(currentDate);
    } else if (calendarView === 'week') {
      return getWeekDays(currentDate);
    } else {
      return getDaysInMonth(currentDate);
    }
  }, [currentDate, calendarView]);

  const getPostsForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return posts.filter(post => post.date === dateStr);
  };

  // Gerar slots de horário (13:00 às 23:30, intervalos de 30 min)
  const generateTimeSlots = () => {
    const slots: string[] = [];
    for (let hour = 13; hour <= 23; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      if (hour < 23) {
        slots.push(`${hour.toString().padStart(2, '0')}:30`);
      }
    }
    return slots;
  };

  const timeSlots = useMemo(() => generateTimeSlots(), []);

  // Encontrar posts em um horário específico para um dia
  const getPostsForTimeSlot = (date: Date, timeSlot: string) => {
    const dateStr = date.toISOString().split('T')[0];
    const [slotHour, slotMinute] = timeSlot.split(':').map(Number);
    
    return posts.filter(post => {
      if (post.date !== dateStr) return false;
      
      const [postHour, postMinute] = post.time.split(':').map(Number);
      const postTotalMinutes = postHour * 60 + postMinute;
      const slotTotalMinutes = slotHour * 60 + slotMinute;
      
      // Post aparece no slot se estiver dentro da janela de 30 minutos
      return postTotalMinutes >= slotTotalMinutes && postTotalMinutes < slotTotalMinutes + 30;
    });
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = () => {
      if (isDropdownOpen) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handlePrevMonth = () => {
    if (calendarView === 'day') {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() - 1);
      setCurrentDate(newDate);
    } else if (calendarView === 'week') {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() - 7);
      setCurrentDate(newDate);
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
    }
  };

  const handleNextMonth = () => {
    if (calendarView === 'day') {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() + 1);
      setCurrentDate(newDate);
    } else if (calendarView === 'week') {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() + 7);
      setCurrentDate(newDate);
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleDayClick = (date: Date) => {
    if (!canEdit) {
      return;
    }

    const dateStr = date.toISOString().split('T')[0];
    setFormData(prev => ({
      ...prev,
      date: dateStr,
      time: '08:00'
    }));
    setEditingPost(null);
    setIsModalOpen(true);
  };

  const handlePostClick = (post: Post) => {
    if (!canEdit) {
      return;
    }

    setEditingPost(post);
    setFormData({
      profileName: post.profileName,
      date: post.date,
      time: post.time,
      text: post.text,
      images: [...post.images],
      status: post.status,
      tone: post.tone,
      emoticons: post.emoticons,
      wordCount: post.wordCount
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPost(null);
    setFormData({
      text: '',
      images: [],
      tone: 'automatic',
      emoticons: 'automatic',
      wordCount: 25,
      status: 'scheduled',
      profileName: 'Bill Car Rent a Car'
    });
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const newImages: string[] = [];
      Array.from(files).forEach(file => {
        const reader = new FileReader();
        reader.onload = (event) => {
          if (event.target?.result) {
            newImages.push(event.target.result as string);
            if (newImages.length === files.length) {
              setFormData(prev => ({
                ...prev,
                images: [...(prev.images || []), ...newImages]
              }));
            }
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleRemoveImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images?.filter((_, i) => i !== index) || []
    }));
  };

  const generateAIText = () => {
    setGeneratingAI(true);
    
    const templates = [
      "🚗 Procurando o carro perfeito para sua viagem? Estamos aqui para ajudar! Oferecemos as melhores condições e uma frota moderna. Entre em contato e reserve já o seu! #AluguelDeCarros #Viagem",
      "✨ Novidade! Carros novos chegando em nossa frota. Aproveite condições especiais para locações de longa duração. Fale conosco e garanta o seu! 🚙 #CarrosNovos #AluguelEspecial",
      "🌟 Final de semana chegando e você ainda não reservou seu carro? Não perca tempo! Temos opções para todos os estilos e bolsos. Reserve agora! #FimDeSemana #Mobilidade",
      "💼 Para sua empresa ou lazer, temos a solução perfeita em locação de veículos. Atendimento personalizado e frota completa. Consulte-nos! #Empresarial #Locacao"
    ];

    setTimeout(() => {
      const randomTemplate = templates[Math.floor(Math.random() * templates.length)];
      setFormData(prev => ({
        ...prev,
        text: randomTemplate
      }));
      setGeneratingAI(false);
      toast.success('Texto gerado com IA!');
    }, 1500);
  };

  const improveText = () => {
    if (!formData.text) {
      toast.warning('Digite um texto primeiro para melhorar');
      return;
    }

    setGeneratingAI(true);

    setTimeout(() => {
      const improved = `${formData.text}\n\n✨ Atendimento personalizado\n🚗 Frota moderna e revisada\n💰 Melhores preços da região\n\nFale conosco e faça sua reserva!`;
      setFormData(prev => ({
        ...prev,
        text: improved
      }));
      setGeneratingAI(false);
      toast.success('Texto melhorado!');
    }, 1500);
  };

  const showIdeas = () => {
    toast.info('Ideias: Promoções de final de semana, Novos carros na frota, Dicas de viagem, Benefícios de alugar conosco', {
      autoClose: 5000
    });
  };

  const handleSavePost = (status: 'draft' | 'scheduled') => {
    if (!formData.text || !formData.date || !formData.time) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    const postData: Post = {
      id: editingPost?.id || `post-${Date.now()}`,
      profileName: formData.profileName || 'Bill Car Rent a Car',
      date: formData.date!,
      time: formData.time!,
      text: formData.text!,
      images: formData.images || [],
      status: status,
      tone: formData.tone,
      emoticons: formData.emoticons,
      wordCount: formData.wordCount,
      createdAt: editingPost?.createdAt || new Date().toISOString(),
      updatedAt: editingPost ? new Date().toISOString() : undefined
    };

    if (editingPost) {
      setPosts(prev => prev.map(p => p.id === editingPost.id ? postData : p));
      toast.success('Postagem atualizada!');
    } else {
      setPosts(prev => [...prev, postData]);
      toast.success(status === 'draft' ? 'Rascunho salvo!' : 'Publicação agendada!');
    }

    handleCloseModal();
  };

  const formatDate = (dateStr: string, timeStr: string) => {
    const date = new Date(`${dateStr}T${timeStr}`);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSaveProfile = async (updatedProfile: Profile) => {
    try {
      await profilesTrackingService.updateProfile(
        updatedProfile.id,
        updatedProfile,
      );
      setProfileData(updatedProfile);
      toast.success('Perfil atualizado com sucesso!');
    } catch (error) {
      toast.error('Erro ao atualizar perfil');
      console.error(error);
      throw error;
    }
  };

  return (
    <PostsContainer>
      <Sidebar />
      <MainContent>
        <Header>
          <HeaderTop>
            <HeaderTitle>
              <Title>Postagens</Title>
              <Subtitle>Agende e gerencie suas publicações nas redes sociais</Subtitle>
            </HeaderTitle>

            <CalendarControls>
              <MonthNavigation>
                <NavButton onClick={handlePrevMonth}>
                  <span className="material-icons">chevron_left</span>
                </NavButton>
                <CurrentMonth>{currentMonthYear}</CurrentMonth>
                <NavButton onClick={handleNextMonth}>
                  <span className="material-icons">chevron_right</span>
                </NavButton>
              </MonthNavigation>

              <TodayButton onClick={handleToday}>Hoje</TodayButton>

              <ViewDropdown>
                <DropdownButton onClick={(e) => {
                  e.stopPropagation();
                  setIsDropdownOpen(!isDropdownOpen);
                }}>
                  {viewMode === 'calendar' && 'Calendário'}
                  {viewMode === 'list' && 'Lista'}
                  <span className="material-icons">
                    {isDropdownOpen ? 'expand_less' : 'expand_more'}
                  </span>
                </DropdownButton>
                <DropdownMenu $isOpen={isDropdownOpen}>
                  <DropdownItem 
                    $active={viewMode === 'calendar'}
                    onClick={(e) => {
                      e.stopPropagation();
                      setViewMode('calendar');
                      setIsDropdownOpen(false);
                    }}
                  >
                    Calendário
                  </DropdownItem>
                  <DropdownItem 
                    $active={viewMode === 'list'}
                    onClick={(e) => {
                      e.stopPropagation();
                      setViewMode('list');
                      setIsDropdownOpen(false);
                    }}
                  >
                    Lista
                  </DropdownItem>
                </DropdownMenu>
              </ViewDropdown>

              {viewMode === 'calendar' && (
                <ViewButtons>
                  <ViewButton $active={calendarView === 'day'} onClick={() => setCalendarView('day')}>
                    Dia
                  </ViewButton>
                  <ViewButton $active={calendarView === 'week'} onClick={() => setCalendarView('week')}>
                    Semana
                  </ViewButton>
                  <ViewButton $active={calendarView === 'month'} onClick={() => setCalendarView('month')}>
                    Mês
                  </ViewButton>
                </ViewButtons>
              )}
            </CalendarControls>
          </HeaderTop>
        </Header>

        {/* Visualização em Lista */}
        {viewMode === 'list' && (
          <ListView>
            <ListHeader>
              <ListTitle>Todas as Postagens</ListTitle>
              {canEdit && (
                <AddButton onClick={() => setIsModalOpen(true)}>
                  <span className="material-icons">add</span>
                  Nova Postagem
                </AddButton>
              )}
            </ListHeader>

            {posts.length > 0 ? (
              <PostsList>
                {posts
                  .sort((a, b) => new Date(`${a.date}T${a.time}`).getTime() - new Date(`${b.date}T${b.time}`).getTime())
                  .map(post => (
                    <PostCard key={post.id} onClick={() => handlePostClick(post)}>
                      <PostCardHeader>
                        <PostCardInfo>
                          <PostCardDate>
                            {new Date(post.date).toLocaleDateString('pt-BR', { 
                              day: '2-digit', 
                              month: 'long', 
                              year: 'numeric' 
                            })}
                          </PostCardDate>
                          <PostCardTime>{post.time}</PostCardTime>
                        </PostCardInfo>
                        <PostCardStatus $status={post.status}>
                          {post.status === 'published' && 'Publicado'}
                          {post.status === 'scheduled' && 'Agendado'}
                          {post.status === 'draft' && 'Rascunho'}
                        </PostCardStatus>
                      </PostCardHeader>
                      <PostCardContent>
                        {post.text}
                      </PostCardContent>
                      {post.images && post.images.length > 0 && (
                        <PostCardImages>
                          {post.images.map((image, idx) => (
                            <PostCardImage key={idx} src={image} alt={`Imagem ${idx + 1}`} />
                          ))}
                        </PostCardImages>
                      )}
                    </PostCard>
                  ))}
              </PostsList>
            ) : (
              <EmptyState>
                <EmptyStateIcon className="material-icons">post_add</EmptyStateIcon>
                <EmptyStateText>Nenhuma postagem ainda</EmptyStateText>
                {canEdit && (
                  <AddButton onClick={() => setIsModalOpen(true)}>
                    <span className="material-icons">add</span>
                    Criar Primeira Postagem
                  </AddButton>
                )}
              </EmptyState>
            )}
          </ListView>
        )}

        {/* Visualização em Calendário */}
        {viewMode === 'calendar' && (
          <>
            {(calendarView === 'day' || calendarView === 'week') ? (
              // Grade de Horários para Dia e Semana
              <CalendarWrapper>
                <TimeGridContainer>
                  <TimeGridHeader>
                    <TimeGridHeaderCell>Hora</TimeGridHeaderCell>
                    {getDisplayDays.map((day, index) => (
                      <TimeGridHeaderCell key={index}>
                        {calendarView === 'day' 
                          ? day.date.toLocaleDateString('pt-BR', { weekday: 'long' })
                          : day.date.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' })
                        }
                      </TimeGridHeaderCell>
                    ))}
                  </TimeGridHeader>
                  <TimeGridScroll>
                    <TimeGridBody>
                      {timeSlots.map((timeSlot) => (
                        <TimeGridRow key={timeSlot} $columns={getDisplayDays.length}>
                          <TimeSlot>{timeSlot}</TimeSlot>
                          {getDisplayDays.map((day, dayIndex) => {
                            const postsInSlot = getPostsForTimeSlot(day.date, timeSlot);
                            return (
                              <DayColumn 
                                key={`${timeSlot}-${dayIndex}`}
                                onClick={() => {
                                  if (!canEdit) {
                                    return;
                                  }

                                  const dateStr = day.date.toISOString().split('T')[0];
                                  setFormData(prev => ({
                                    ...prev,
                                    date: dateStr,
                                    time: timeSlot
                                  }));
                                  setEditingPost(null);
                                  setIsModalOpen(true);
                                }}
                              >
                                {postsInSlot.map(post => (
                                  <TimePost
                                    key={post.id}
                                    $status={post.status}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePostClick(post);
                                    }}
                                  >
                                    <TimePostTime>{post.time}</TimePostTime>
                                    <TimePostText>{post.text}</TimePostText>
                                  </TimePost>
                                ))}
                              </DayColumn>
                            );
                          })}
                        </TimeGridRow>
                      ))}
                    </TimeGridBody>
                  </TimeGridScroll>
                </TimeGridContainer>
              </CalendarWrapper>
            ) : (
              // Visualização de Mês (Grid tradicional)
              <CalendarWrapper>
                <Calendar>
                  <CalendarHeader>
                    {weekDays.map(day => (
                      <WeekDay key={day}>{day}</WeekDay>
                    ))}
                  </CalendarHeader>

                  <CalendarGrid $viewType={calendarView}>
                    {getDisplayDays.map((day, index) => {
                      const dayPosts = getPostsForDate(day.date);
                      return (
                        <DayCell
                          key={index}
                          $isToday={isToday(day.date)}
                          $isOtherMonth={!day.isCurrentMonth}
                          $viewType={calendarView}
                          onClick={() => handleDayClick(day.date)}
                        >
                          <DayNumber $isToday={isToday(day.date)}>
                            {day.date.getDate()}
                          </DayNumber>
                          <PostIndicators>
                            {dayPosts.map(post => (
                              <PostBadge
                                key={post.id}
                                $status={post.status}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePostClick(post);
                                }}
                              >
                                {post.time} - {post.text.substring(0, 20)}...
                              </PostBadge>
                            ))}
                          </PostIndicators>
                        </DayCell>
                      );
                    })}
                  </CalendarGrid>
                </Calendar>
              </CalendarWrapper>
            )}
          </>
        )}

        {/* Modal de Postagem */}
        {canEdit && isModalOpen && (
          <ModalOverlay onClick={handleCloseModal}>
            <ModalContainer onClick={(e) => e.stopPropagation()}>
              <ModalLeft>
                <ModalHeader>
                  <ModalTitle>
                    {editingPost ? 'Editar Postagem' : 'Nova Postagem'}
                  </ModalTitle>
                  <CloseButton onClick={handleCloseModal}>
                    <span className="material-icons">close</span>
                  </CloseButton>
                </ModalHeader>

                <TabsContainer>
                  <Tab $active={activeTab === 'update'} onClick={() => setActiveTab('update')}>
                    Atualização
                  </Tab>
                  <Tab $active={activeTab === 'offer'} onClick={() => setActiveTab('offer')}>
                    Oferta
                  </Tab>
                  <Tab $active={activeTab === 'event'} onClick={() => setActiveTab('event')}>
                    Evento
                  </Tab>
                </TabsContainer>

                <ModalBody>
                  {activeTab === 'update' && (
                    <>
                      <FormSection>
                        <Label>Texto da Postagem</Label>
                        <TextArea
                          value={formData.text || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, text: e.target.value }))}
                          placeholder="Digite o texto da postagem ou ideia com palavras-chave relevantes e clique em Criar Texto"
                          disabled={generatingAI}
                        />
                        <CharCounter>{formData.text?.length || 0}/1500 caracteres</CharCounter>
                      </FormSection>

                      <FormSection>
                        <Label>Imagens</Label>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageUpload}
                          style={{ display: 'none' }}
                          id="image-upload"
                        />
                        <ImageUploadArea onClick={() => document.getElementById('image-upload')?.click()}>
                          <UploadIcon className="material-icons">
                            add_photo_alternate
                          </UploadIcon>
                          <UploadText>
                            Arraste uma imagem aqui ou clique para selecionar
                          </UploadText>
                          <UploadHint>
                            PNG, JPG ou JPEG até 10MB
                          </UploadHint>
                          <AIImageButton 
                            type="button" 
                            onClick={(e) => {
                              e.stopPropagation();
                              toast.info('Pesquisa de imagens com IA em breve!');
                            }}
                          >
                            <span className="material-icons">auto_awesome</span>
                            Pesquisar Imagens com IA
                          </AIImageButton>
                        </ImageUploadArea>

                        {formData.images && formData.images.length > 0 && (
                          <ImagePreviewContainer>
                            {formData.images.map((image, index) => (
                              <ImagePreview key={index}>
                                <img src={image} alt={`Preview ${index + 1}`} />
                                <RemoveImageButton onClick={() => handleRemoveImage(index)}>
                                  <span className="material-icons">close</span>
                                </RemoveImageButton>
                              </ImagePreview>
                            ))}
                          </ImagePreviewContainer>
                        )}
                      </FormSection>

                      <FormSection>
                        <Label>Criar conteúdo com IA:</Label>
                        <AIButtonsContainer>
                          <AIButton onClick={generateAIText} disabled={generatingAI}>
                            <span className="material-icons">auto_awesome</span>
                            {generatingAI ? 'Gerando...' : 'Criar Texto'}
                          </AIButton>
                          <SecondaryButton onClick={improveText} disabled={generatingAI}>
                            <span className="material-icons">edit</span>
                            Melhorar Texto
                          </SecondaryButton>
                          <SecondaryButton onClick={showIdeas}>
                            <span className="material-icons">lightbulb</span>
                            Mostrar Ideias
                          </SecondaryButton>
                        </AIButtonsContainer>
                      </FormSection>
                    </>
                  )}

                  {activeTab === 'offer' && (
                    <>
                      <FormSection>
                        <Label>Título da Oferta</Label>
                        <Input
                          type="text"
                          value={offerData.title}
                          onChange={(e) => setOfferData(prev => ({ ...prev, title: e.target.value }))}
                          placeholder="Ex: 20% de desconto em todos os carros"
                        />
                      </FormSection>

                      <FormSection>
                        <Label>Descrição e Detalhes da Oferta</Label>
                        <TextArea
                          value={offerData.description}
                          onChange={(e) => setOfferData(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Descreva os detalhes da sua oferta..."
                          disabled={generatingAI}
                        />
                        <CharCounter>{offerData.description?.length || 0}/1500 caracteres</CharCounter>
                      </FormSection>

                      <FormSection>
                        <Label>Imagens</Label>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageUpload}
                          style={{ display: 'none' }}
                          id="image-upload-offer"
                        />
                        <ImageUploadArea onClick={() => document.getElementById('image-upload-offer')?.click()}>
                          <UploadIcon className="material-icons">
                            add_photo_alternate
                          </UploadIcon>
                          <UploadText>
                            Arraste uma imagem aqui ou clique para selecionar
                          </UploadText>
                          <UploadHint>
                            PNG, JPG ou JPEG até 10MB
                          </UploadHint>
                          <AIImageButton 
                            type="button" 
                            onClick={(e) => {
                              e.stopPropagation();
                              toast.info('Pesquisa de imagens com IA em breve!');
                            }}
                          >
                            <span className="material-icons">auto_awesome</span>
                            Pesquisar Imagens com IA
                          </AIImageButton>
                        </ImageUploadArea>

                        {formData.images && formData.images.length > 0 && (
                          <ImagePreviewContainer>
                            {formData.images.map((image, index) => (
                              <ImagePreview key={index}>
                                <img src={image} alt={`Preview ${index + 1}`} />
                                <RemoveImageButton onClick={() => handleRemoveImage(index)}>
                                  <span className="material-icons">close</span>
                                </RemoveImageButton>
                              </ImagePreview>
                            ))}
                          </ImagePreviewContainer>
                        )}
                      </FormSection>

                      <FormSection>
                        <DateTimeGroup>
                          <div>
                            <Label>Data de Início*</Label>
                            <Input
                              type="date"
                              value={offerData.startDate}
                              onChange={(e) => setOfferData(prev => ({ ...prev, startDate: e.target.value }))}
                            />
                          </div>
                          <div>
                            <Label>Data de Fim*</Label>
                            <Input
                              type="date"
                              value={offerData.endDate}
                              onChange={(e) => setOfferData(prev => ({ ...prev, endDate: e.target.value }))}
                            />
                          </div>
                        </DateTimeGroup>
                      </FormSection>

                      <FormSection>
                        <Label>Código do Cupom (opcional)</Label>
                        <Input
                          type="text"
                          value={offerData.couponCode}
                          onChange={(e) => setOfferData(prev => ({ ...prev, couponCode: e.target.value }))}
                          placeholder="Ex: DESCONTO20"
                        />
                      </FormSection>

                      <FormSection>
                        <Label>Link para resgatar a oferta (opcional)</Label>
                        <Input
                          type="url"
                          value={offerData.redeemLink}
                          onChange={(e) => setOfferData(prev => ({ ...prev, redeemLink: e.target.value }))}
                          placeholder="https://..."
                        />
                      </FormSection>

                      <FormSection>
                        <Label>Termos e condições (opcional)</Label>
                        <TextArea
                          value={offerData.terms}
                          onChange={(e) => setOfferData(prev => ({ ...prev, terms: e.target.value }))}
                          placeholder="Descreva os termos e condições da oferta..."
                          style={{ minHeight: '80px' }}
                        />
                      </FormSection>
                    </>
                  )}

                  {activeTab === 'event' && (
                    <>
                      <FormSection>
                        <Label>Título do Evento</Label>
                        <Input
                          type="text"
                          value={eventData.title}
                          onChange={(e) => setEventData(prev => ({ ...prev, title: e.target.value }))}
                          placeholder="Ex: Inauguração da nova loja"
                        />
                      </FormSection>

                      <FormSection>
                        <Label>Descrição e Detalhes do Evento</Label>
                        <TextArea
                          value={eventData.description}
                          onChange={(e) => setEventData(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Descreva os detalhes do seu evento..."
                          disabled={generatingAI}
                        />
                        <CharCounter>{eventData.description?.length || 0}/1500 caracteres</CharCounter>
                      </FormSection>

                      <FormSection>
                        <Label>Imagens</Label>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageUpload}
                          style={{ display: 'none' }}
                          id="image-upload-event"
                        />
                        <ImageUploadArea onClick={() => document.getElementById('image-upload-event')?.click()}>
                          <UploadIcon className="material-icons">
                            add_photo_alternate
                          </UploadIcon>
                          <UploadText>
                            Arraste uma imagem aqui ou clique para selecionar
                          </UploadText>
                          <UploadHint>
                            PNG, JPG ou JPEG até 10MB
                          </UploadHint>
                          <AIImageButton 
                            type="button" 
                            onClick={(e) => {
                              e.stopPropagation();
                              toast.info('Pesquisa de imagens com IA em breve!');
                            }}
                          >
                            <span className="material-icons">auto_awesome</span>
                            Pesquisar Imagens com IA
                          </AIImageButton>
                        </ImageUploadArea>

                        {formData.images && formData.images.length > 0 && (
                          <ImagePreviewContainer>
                            {formData.images.map((image, index) => (
                              <ImagePreview key={index}>
                                <img src={image} alt={`Preview ${index + 1}`} />
                                <RemoveImageButton onClick={() => handleRemoveImage(index)}>
                                  <span className="material-icons">close</span>
                                </RemoveImageButton>
                              </ImagePreview>
                            ))}
                          </ImagePreviewContainer>
                        )}
                      </FormSection>

                      <FormSection>
                        <DateTimeGroup>
                          <div>
                            <Label>Data de Início:</Label>
                            <Input
                              type="date"
                              value={eventData.startDate}
                              onChange={(e) => setEventData(prev => ({ ...prev, startDate: e.target.value }))}
                            />
                          </div>
                          <div>
                            <Label>Data de Fim:</Label>
                            <Input
                              type="date"
                              value={eventData.endDate}
                              onChange={(e) => setEventData(prev => ({ ...prev, endDate: e.target.value }))}
                            />
                          </div>
                        </DateTimeGroup>
                      </FormSection>

                      <FormSection>
                        <DateTimeGroup>
                          <div>
                            <Label>Horário de Início:</Label>
                            <Input
                              type="time"
                              value={eventData.startTime}
                              onChange={(e) => setEventData(prev => ({ ...prev, startTime: e.target.value }))}
                            />
                          </div>
                          <div>
                            <Label>Horário de Fim:</Label>
                            <Input
                              type="time"
                              value={eventData.endTime}
                              onChange={(e) => setEventData(prev => ({ ...prev, endTime: e.target.value }))}
                            />
                          </div>
                        </DateTimeGroup>
                      </FormSection>
                    </>
                  )}

                  {/* Campos comuns a todas as abas */}
                  <FormSection>
                    <Label>Botão:</Label>
                    <Select
                      value="none"
                      onChange={() => {}}
                    >
                      <option value="none">Nenhum</option>
                      <option value="learn-more">Saiba Mais</option>
                      <option value="call">Ligar</option>
                      <option value="book">Reservar</option>
                    </Select>
                  </FormSection>

                  <FormSection>
                    <Label>Data e Hora:</Label>
                    <DateTimeGroup>
                      <Input
                        type="date"
                        value={formData.date || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                      />
                      <Input
                        type="time"
                        value={formData.time || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                      />
                    </DateTimeGroup>
                  </FormSection>

                  {activeTab === 'update' && (
                    <>
                      <FormSection>
                        <Label>Tom:</Label>
                        <Select
                          value={formData.tone || 'automatic'}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            tone: e.target.value as 'automatic' | 'formal' | 'casual' | 'professional'
                          }))}
                        >
                          <option value="automatic">Automático</option>
                          <option value="formal">Formal</option>
                          <option value="casual">Casual</option>
                          <option value="professional">Profissional</option>
                        </Select>
                      </FormSection>

                      <FormSection>
                        <Label>Emoticons:</Label>
                        <Select
                          value={formData.emoticons || 'automatic'}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            emoticons: e.target.value as 'automatic' | 'yes' | 'no'
                          }))}
                        >
                          <option value="automatic">Automático</option>
                          <option value="yes">Sim</option>
                          <option value="no">Não</option>
                        </Select>
                      </FormSection>

                      <FormSection>
                        <Label>Quantidade de Palavras:</Label>
                        <Select
                          value={formData.wordCount || 25}
                          onChange={(e) => setFormData(prev => ({ ...prev, wordCount: Number(e.target.value) }))}
                        >
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={75}>75</option>
                          <option value={100}>100</option>
                        </Select>
                      </FormSection>
                    </>
                  )}
                </ModalBody>

                <ModalFooter>
                  <FooterLeft>
                    <CancelButton onClick={handleCloseModal}>
                      Cancelar
                    </CancelButton>
                  </FooterLeft>
                  <FooterRight>
                    <SaveButton variant="secondary" onClick={() => handleSavePost('draft')}>
                      Salvar Rascunho
                    </SaveButton>
                    <SaveButton onClick={() => handleSavePost('scheduled')}>
                      Agendar Publicação
                    </SaveButton>
                  </FooterRight>
                </ModalFooter>
              </ModalLeft>

              <ModalRight>
                <PreviewHeader>
                  <PreviewTitle>
                    <span className="material-icons">visibility</span>
                    Pré-visualização da Postagem
                  </PreviewTitle>
                </PreviewHeader>

                <PreviewBody>
                  {(formData.text || offerData.title || offerData.description || eventData.title || eventData.description || formData.images?.length) ? (
                    <PostPreviewCard>
                      <PostPreviewHeader>
                        <ProfileIcon>
                          {formData.profileName?.substring(0, 2).toUpperCase() || 'BC'}
                        </ProfileIcon>
                        <ProfileInfo>
                          <ProfileName>{formData.profileName || 'Bill Car Rent a Car'}</ProfileName>
                          <PostDate>
                            {formData.date && formData.time 
                              ? formatDate(formData.date, formData.time)
                              : 'Selecione data e hora'
                            }
                          </PostDate>
                        </ProfileInfo>
                      </PostPreviewHeader>

                      <PostPreviewContent>
                        {activeTab === 'update' && formData.text && <PostText>{formData.text}</PostText>}
                        
                        {activeTab === 'offer' && (
                          <>
                            {offerData.title && (
                              <PostText style={{ fontWeight: 600, fontSize: '16px', marginBottom: '8px' }}>
                                {offerData.title}
                              </PostText>
                            )}
                            {offerData.description && <PostText>{offerData.description}</PostText>}
                            {(offerData.startDate || offerData.endDate) && (
                              <PostText style={{ fontSize: '12px', color: '#5f6368', marginTop: '8px' }}>
                                {offerData.startDate && `Início: ${new Date(offerData.startDate).toLocaleDateString('pt-BR')}`}
                                {offerData.startDate && offerData.endDate && ' • '}
                                {offerData.endDate && `Fim: ${new Date(offerData.endDate).toLocaleDateString('pt-BR')}`}
                              </PostText>
                            )}
                            {offerData.couponCode && (
                              <PostText style={{ 
                                background: '#e8f0fe', 
                                padding: '8px 12px', 
                                borderRadius: '6px',
                                fontWeight: 600,
                                color: '#1a73e8',
                                marginTop: '8px',
                                display: 'inline-block'
                              }}>
                                Cupom: {offerData.couponCode}
                              </PostText>
                            )}
                          </>
                        )}

                        {activeTab === 'event' && (
                          <>
                            {eventData.title && (
                              <PostText style={{ fontWeight: 600, fontSize: '16px', marginBottom: '8px' }}>
                                📅 {eventData.title}
                              </PostText>
                            )}
                            {eventData.description && <PostText>{eventData.description}</PostText>}
                            {(eventData.startDate || eventData.startTime) && (
                              <PostText style={{ 
                                fontSize: '13px', 
                                color: '#3c4043', 
                                marginTop: '12px',
                                padding: '12px',
                                background: '#f8f9fa',
                                borderRadius: '6px'
                              }}>
                                {eventData.startDate && (
                                  <div style={{ marginBottom: '4px' }}>
                                    <strong>Início:</strong> {new Date(eventData.startDate).toLocaleDateString('pt-BR')}
                                    {eventData.startTime && ` às ${eventData.startTime}`}
                                  </div>
                                )}
                                {eventData.endDate && (
                                  <div>
                                    <strong>Fim:</strong> {new Date(eventData.endDate).toLocaleDateString('pt-BR')}
                                    {eventData.endTime && ` às ${eventData.endTime}`}
                                  </div>
                                )}
                              </PostText>
                            )}
                          </>
                        )}

                        {formData.images && formData.images.length > 0 && (
                          <PostImages $count={formData.images.length}>
                            {formData.images.map((image, index) => (
                              <img key={index} src={image} alt={`Post ${index + 1}`} />
                            ))}
                          </PostImages>
                        )}
                      </PostPreviewContent>
                    </PostPreviewCard>
                  ) : (
                    <EmptyPreview>
                      <span className="material-icons">description</span>
                      <p>A prévia da postagem aparecerá aqui</p>
                    </EmptyPreview>
                  )}
                </PreviewBody>
              </ModalRight>
            </ModalContainer>
          </ModalOverlay>
        )}
      </MainContent>

      {/* Modal de Edição de Perfil */}
      <EditProfileModal
        isOpen={isEditModalOpen}
        profile={profileData}
        onClose={closeModal}
        onSave={handleSaveProfile}
        hideIATab={true}
      />
    </PostsContainer>
  );
};

export default PostsPage;
